import { makeAutoObservable } from 'mobx';
import filterRowFactory from './filterRowFactory';
import {calendarDate} from 'shared/tools';
import _ from 'lodash';
import {DEFAULT_DATE, DEFAULT_START_DATE, DEFAULT_END_DATE} from 'main/containers/reports/tools';

class CompositeReportState {
  filters = {};
  availableFilterCategories = [];
  dateFilterType;
  dateFilterFormat;
  defaultStartDate;
  defaultEndDate;
  count;
  reports = [];
  filterRows = [];
  categoryFilterOpen = false;
  dateFilterOpen = false;
  disableFilterEditing = false;

  date;
  startDate;
  endDate;

  constructor(filterCategories, dateFilterType, dateFilterFormat, defaultStartDate, defaultEndDate, filterRows, disableFilterEditing) {
    makeAutoObservable(this);

    this.availableFilterCategories = filterCategories || [];
    this.dateFilterType = dateFilterType;
    this.dateFilterFormat = dateFilterFormat;
    this.defaultStartDate = defaultStartDate || DEFAULT_START_DATE;
    this.defaultEndDate = defaultEndDate || DEFAULT_END_DATE;
    this.disableFilterEditing = disableFilterEditing;

    if (!_.isEmpty(filterRows)) {
      this.filterRows = filterRows;
    }
  }

  filterCategorySelected(category) {
    return !!_.find(this.filterRows, {category});
  }

  resetChanges() {
    this.date = this.filters.date;
    this.startDate = this.filters.startDate;
    this.endDate = this.filters.endDate;
  }

  closeFilters() {
    this.categoryFilterOpen = false;
    this.dateFilterOpen = false;
    this.resetChanges();
    for (const row of this.filterRows) {
      row.closeDropdowns();
    }
  }

  openCategoryFilter() {
    this.closeFilters();
    this.resetChanges();
    this.categoryFilterOpen = true;
  }

  openDateFilter() {
    this.closeFilters();
    this.resetChanges();
    this.dateFilterOpen = true;
  }

  closeCategoryFilter() {
    this.resetChanges();
    this.categoryFilterOpen = false;
  }

  closeDateFilter() {
    this.resetChanges();
    this.dateFilterOpen = false;
  }

  applyFilters() {
    this.reports.clear();

    if (this.invalidDate) this.fallBackToDefaultDate();
    if (this.invalidDateRange) this.fallBackToDefaultDateRange();

    this.filters = {
      date: this.date,
      startDate: this.startDate,
      endDate: this.endDate,
      filter: _.flatten(this.filterRows.map(viewModel => viewModel.row.toJS()))
    };
    this.closeFilters();
  }

  fallBackToDefaultDate() {
    if (this.dateFilterType !== 'single') return;

    this.date = DEFAULT_DATE;
  }

  fallBackToDefaultDateRange() {
    if (this.dateFilterType !== 'range') return;

    this.startDate = this.startDate || this.defaultStartDate;
    this.endDate = this.endDate || this.defaultEndDate;
  }

  updateDate(date) {
    if (!_.get(this.filters, 'date')) {
      this.filters = {
        date: date
      };
    }

    this.date = date;
  }

  updateDateRange(startDate, endDate) {
    if (!_.get(this.filters, 'startDate')) {
      this.filters = {
        startDate: startDate,
        endDate: endDate
      };
    }

    this.startDate = startDate;
    this.endDate = endDate;
  }

  updateCount(count) {
    this.count = count;
  }

  addReport(report) {
    this.reports.push(report);
  }

  toggleFilterRow(filterCategory, checked) {
    if (checked) {
      const rows = filterRowFactory({category: filterCategory});
      if (_.isArray(rows)) {
        this.filterRows.push(...rows);
      } else {
        this.filterRows.push(rows);
      }
      this.closeCategoryFilter();
    } else {
      const [removedRow] = _.remove(this.filterRows, {category: filterCategory});
      if (_.find(this.filters.filter, {attribute: removedRow.row.attribute})) {
        this.applyFilters();
      }
    }
  }

  get invalidDate() {
    return !this.date;
  }

  get invalidDateRange() {
    return !this.startDate || !this.endDate;
  }

  get hasFilters() {
    return this.dateFilterType || this.hasFilterCategories || this.hasCount;
  }

  get resultCountView() {
    return {
      id: 'reports.RESULT_COUNT',
      values: {
        count: this.reportWithCount.meta.totalCount
      }
    };
  }

  get hasCount() {
    return !!this.reportWithCount;
  }

  get reportWithCount() {
    return _.find(this.reports, r => _.isNumber(_.get(r, 'meta.totalCount')) && r.reportType !== 'metric');
  }

  get dateFilterView() {
    const options = {dateFilterFormat: this.dateFilterFormat};
    return this.dateFilterType === 'single' ? calendarDate(this.date, options) : `${calendarDate(this.startDate, options)} - ${calendarDate(this.endDate, options)}`;
  }

  get hasDateFilter() {
    return !!this.dateFilterType;
  }

  get hasFilterCategories() {
    return !_.isEmpty(this.availableFilterCategories);
  }

  get hasFiltersApplied() {
    return !_.isEmpty(this.filters.filter);
  }

  get showFilterRows() {
    return !_.isEmpty(this.filterRows);
  }
}

export default CompositeReportState;
